import { Column } from "@/components/Column";

const SIDING_COLOR = "#5d4a6e";
const SIDING_LINE_COLOR = "#4a3a5a";
const ROOF_COLOR = "#3a2c48";
const SHINGLE_LINE_COLOR = "#2a1f36";
const TRIM_COLOR = "#9a88b4";
const FRAME_COLOR = "#2a1f33";
const WOOD_COLOR = "#7a5f4a";
const DARK_PANE_COLOR = "#2b3352";
const LIT_PANE_COLOR = "#ffb347";
// Windows light once the pumpkins are about a third lit.
const LIGHTS_ON_PROGRESS = 0.3;
const WINDOWS = [
  { x: 30, y: 64, width: 10, height: 13 },
  { x: 58, y: 63, width: 10, height: 13 },
  { x: 85, y: 54, width: 9, height: 12 },
  { x: 85, y: 78, width: 9, height: 12 },
];

// A crooked Victorian on the back-left ridge, above the pond: purple siding,
// shingled roofs, a spire tower and a porch lantern. Windows light at dusk.
export const PlayyardHauntedHouse = ({
  eveningProgress,
}: {
  eveningProgress: number;
}) => {
  const isLit = eveningProgress > LIGHTS_ON_PROGRESS;
  const paneColor = isLit ? LIT_PANE_COLOR : DARK_PANE_COLOR;

  return (
    <>
      <svg
        className="pointer-events-none absolute left-[15%] top-[56.5%] z-[3] w-[15cqw] -translate-x-1/2 -translate-y-full"
        viewBox="0 0 140 112"
      >
        <defs>
          <pattern
            id="playyard-house-siding"
            width="6"
            height="5"
            patternUnits="userSpaceOnUse"
          >
            <rect width="6" height="5" fill={SIDING_COLOR} />
            <rect y="4" width="6" height="1" fill={SIDING_LINE_COLOR} />
          </pattern>
          <pattern
            id="playyard-house-shingles"
            width="6"
            height="4"
            patternUnits="userSpaceOnUse"
          >
            <rect width="6" height="4" fill={ROOF_COLOR} />
            <path
              d="M0 4 Q1.5 1.5 3 4 Q4.5 1.5 6 4"
              stroke={SHINGLE_LINE_COLOR}
              strokeWidth="0.8"
              fill="none"
            />
          </pattern>
        </defs>

        {/* Chimney */}
        <path d="M31 46 L31 24 L40 24 L40 40Z" fill="#7a4f52" />
        <path d="M29 22 L42 22 L42 25 L29 25Z" fill={ROOF_COLOR} />

        {/* Main house */}
        <path
          d="M24 112 L26 58 L78 55 L80 112Z"
          fill="url(#playyard-house-siding)"
        />
        <path d="M26 58 L24 112" stroke={TRIM_COLOR} strokeWidth="1.6" />
        <path
          d="M15 61 L50 26 L88 58Z"
          fill="url(#playyard-house-shingles)"
        />
        <path
          d="M15 61 L50 26 L88 58"
          stroke={TRIM_COLOR}
          strokeWidth="1.6"
          fill="none"
          strokeLinejoin="round"
        />
        <circle cx="50" cy="47" r="5" fill={paneColor} />
        <circle
          cx="50"
          cy="47"
          r="5"
          stroke={FRAME_COLOR}
          strokeWidth="1.4"
          fill="none"
        />

        {/* Tower */}
        <path
          d="M80 112 L81 44 L101 44 L103 112Z"
          fill="url(#playyard-house-siding)"
        />
        <path d="M80 112 L81 44 L101 44 L103 112Z" fill="rgb(0 0 0 / 0.12)" />
        <path
          d="M76 47 L91 13 L106 47Z"
          fill="url(#playyard-house-shingles)"
        />
        <path
          d="M76 47 L91 13 L106 47"
          stroke={TRIM_COLOR}
          strokeWidth="1.4"
          fill="none"
          strokeLinejoin="round"
        />
        <path
          d="M91 13 L91 3 M86 6 L96 6 M94 4.5 L96 6 L94 7.5"
          stroke={FRAME_COLOR}
          strokeWidth="1"
          fill="none"
        />

        {/* Windows: frame, pane, muntins */}
        <g
          className={isLit ? "staffer-pet-yard-flicker" : undefined}
          fill={paneColor}
        >
          {WINDOWS.map((pane) => (
            <rect
              key={`${pane.x.toString()}-${pane.y.toString()}`}
              x={pane.x}
              y={pane.y}
              width={pane.width}
              height={pane.height}
            />
          ))}
        </g>
        {WINDOWS.map((pane) => (
          <path
            key={`frame-${pane.x.toString()}-${pane.y.toString()}`}
            d={`M${pane.x.toString()} ${pane.y.toString()} h${pane.width.toString()} v${pane.height.toString()} h${(-pane.width).toString()}Z M${(pane.x + pane.width / 2).toString()} ${pane.y.toString()} v${pane.height.toString()} M${pane.x.toString()} ${(pane.y + pane.height / 2).toString()} h${pane.width.toString()}`}
            stroke={FRAME_COLOR}
            strokeWidth="1.2"
            fill="none"
          />
        ))}
        {/* Shutters: one hangs loose; a plank boards up the tower window */}
        <path d="M26 64 L29.5 64 L29.5 77 L26 77Z" fill="#3f6a5a" />
        <path d="M69 62 L72.5 63 L70 76 L66.5 75Z" fill="#3f6a5a" />
        <path d="M83 82 L96 86" stroke={WOOD_COLOR} strokeWidth="2.4" />

        {/* Porch */}
        <path d="M33 88 L71 88 L75 94 L29 94Z" fill={ROOF_COLOR} />
        <path d="M29 94 L75 94" stroke={TRIM_COLOR} strokeWidth="1.2" />
        <path
          d="M33 94 L33 108 M71 94 L71 108"
          stroke={TRIM_COLOR}
          strokeWidth="1.8"
        />
        <path d="M45 112 L45 99 Q51 92 57 99 L57 112Z" fill="#24172a" />
        <path
          d="M45 112 L45 99 Q51 92 57 99 L57 112"
          stroke={TRIM_COLOR}
          strokeWidth="1"
          fill="none"
        />
        <rect x="40" y="108" width="22" height="4" fill={WOOD_COLOR} />
        <rect
          className="staffer-pet-yard-flicker"
          x="60"
          y="97"
          width="3"
          height="4"
          rx="0.8"
          fill="#ffcf6b"
        />

        {/* Weathering: ivy climbing the left wall and a crack */}
        <path
          d="M26 112 Q22 100 27 94 Q24 86 28 80 Q25 74 29 70"
          stroke="#4f7a3e"
          strokeWidth="2.4"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M74 70 L71 76 L74 80 L72 86"
          stroke={SIDING_LINE_COLOR}
          strokeWidth="0.8"
          fill="none"
        />
      </svg>
      {/* Window glow sits above the night tint so the house reads as lit. */}
      <Column
        className="pointer-events-none absolute left-[15%] top-[48%] z-[39] h-[18%] w-[16cqw] -translate-x-1/2 -translate-y-1/2 mix-blend-screen transition-opacity [transition-duration:1500ms]"
        style={{ opacity: isLit ? 1 : 0 }}
      >
        <Column className="staffer-pet-yard-flicker h-full w-full rounded-[50%] bg-[radial-gradient(ellipse,rgb(255_170_70_/_0.35),transparent_65%)]" />
      </Column>
    </>
  );
};
